/**
 * Trial gating, enforced in the state layer (decision S3).
 *
 * The rule is not a display trick: every selector that feeds the
 * results table, the chart, and the on-screen statistics goes through
 * `visibleDomainsForCell`, and every export request goes through the
 * store's `requestExport`, which refuses while unlicensed. Hiding a
 * button would not be enough; these functions are the gate, and the
 * UI renders what they return.
 *
 * The cap: on the user's own data without a license, at most
 * TRIAL_AREA_CAP (ten) areas of a cell are shown. The built-in demo
 * is exempt and runs the full workflow (S3). A licensed user sees
 * everything. Aggregate counts in Step 4 (how many areas were
 * flagged, suppressed, excluded) are run facts, not area results,
 * and are not capped (OPEN_QUESTIONS Q32).
 */

import type { AnalysisResult, DomainResult } from '@sae/engine';

import type { LicenseState, SourceKind } from './types.js';
import { TRIAL_AREA_CAP } from './types.js';

export function isLicensed(license: LicenseState): boolean {
  return license.status === 'licensed';
}

/** True when the ten-area on-screen cap binds for this project. */
export function capApplies(
  license: LicenseState,
  sourceKind: SourceKind,
): boolean {
  return !isLicensed(license) && sourceKind === 'own';
}

export interface VisibleDomains {
  readonly visible: readonly DomainResult[];
  readonly total: number;
  readonly hiddenCount: number;
  readonly capped: boolean;
}

/**
 * The domains of one model cell that may be shown on screen, in a
 * deterministic area order. This is the only path from a stored
 * result to a results display.
 */
export function visibleDomainsForCell(
  result: AnalysisResult,
  cellId: string,
  license: LicenseState,
  sourceKind: SourceKind,
): VisibleDomains {
  const all = result.domains
    .filter((d) => d.domain.cellId === cellId)
    .slice()
    .sort((a, b) =>
      a.domain.areaId < b.domain.areaId
        ? -1
        : a.domain.areaId > b.domain.areaId
          ? 1
          : 0,
    );
  if (!capApplies(license, sourceKind) || all.length <= TRIAL_AREA_CAP) {
    return { visible: all, total: all.length, hiddenCount: 0, capped: false };
  }
  const visible = all.slice(0, TRIAL_AREA_CAP);
  return {
    visible,
    total: all.length,
    hiddenCount: all.length - visible.length,
    capped: true,
  };
}

/** All cells' domains visible on screen (Step 4 per-area lists). */
export function visibleDomains(
  result: AnalysisResult,
  license: LicenseState,
  sourceKind: SourceKind,
): VisibleDomains {
  const cellIds = [...new Set(result.domains.map((d) => d.domain.cellId))];
  let visible: DomainResult[] = [];
  let total = 0;
  let capped = false;
  for (const cellId of cellIds) {
    const part = visibleDomainsForCell(result, cellId, license, sourceKind);
    visible = visible.concat(part.visible);
    total += part.total;
    capped = capped || part.capped;
  }
  return { visible, total, hiddenCount: total - visible.length, capped };
}

/** Exports are the paid step (S3): unlocked only by an active license. */
export function exportsUnlocked(license: LicenseState): boolean {
  return isLicensed(license);
}
